import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  Trophy,
  Plus,
  FolderOpen,
  BarChart3,
  Settings,
  Clock,
  ChevronRight,
} from "lucide-react";
import { useI18n } from "@tennis-matches/services/i18n";

function buildClubmeisterschaftHref(intent, code) {
  if (intent === "new") return "/clubmeisterschaft/setup?new=1";
  if (intent === "open" && code) {
    return `/clubmeisterschaft?code=${encodeURIComponent(code)}`;
  }
  return "#";
}

function formatDate(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch (_) {
    return null;
  }
}

/**
 * Clubmeisterschaft Landing 1:1 wie Schleifchen (Hero + 4 Action-Tiles + Deine letzten …).
 * Keys: club.*; Routes: clubmeisterschaft-*.
 * Primär: t via Prop/Outlet (Host). Fallback: useI18n aus services (kein core→services).
 * @param {{ t?: Function, recentItems?: Array<{ id?: string, code?: string, label: string, href: string, status?: string, at?: string, updatedAt?: string }>, recentLoading?: boolean, onNewTournament?: () => void }} props
 */
export function ClubmeisterschaftLandingPage({
  t: tProp,
  recentItems: recentItemsProp,
  recentLoading: recentLoadingProp,
  onNewTournament: onNewTournamentProp,
} = {}) {
  const outletContext = useOutletContext() || {};
  const recentItems = recentItemsProp ?? outletContext.recentItems ?? [];
  const recentLoading =
    recentLoadingProp ?? outletContext.recentLoading ?? false;
  const onNewTournament = onNewTournamentProp ?? outletContext.onNewTournament;
  const tHook = useI18n();
  const tr =
    typeof tProp === "function"
      ? tProp
      : typeof outletContext.t === "function"
        ? outletContext.t
        : typeof tHook === "function"
          ? tHook
          : (k) => k;

  const newHref = buildClubmeisterschaftHref("new");
  const openHref = "#";
  const statsHref = "/hub/insights/clubmeisterschaft";
  const settingsHref = "#";

  const actions = [
    {
      titleKey: "actions.newTournament",
      descriptionKey: "club.actionNewDesc",
      href: newHref,
      icon: Plus,
      onBeforeNavigate: onNewTournament,
    },
    {
      titleKey: "actions.openTournament",
      descriptionKey: "club.actionOpenDesc",
      href: openHref,
      icon: FolderOpen,
    },
    {
      titleKey: "actions.stats",
      descriptionKey: "club.actionStatsDesc",
      href: statsHref,
      icon: BarChart3,
    },
    {
      titleKey: "actions.settings",
      descriptionKey: "club.actionSettingsDesc",
      href: settingsHref,
      icon: Settings,
    },
  ];

  const list = recentLoading ? [] : recentItems;
  const emptyKey = recentLoading ? "common.loading" : "club.empty";

  return (
    <div className="page--hub page--hub-clubmeisterschaft">
      <main className="page-container hub-page-main">
        <header className="hub-page-hero">
          <div className="hub-page-hero__icon" aria-hidden="true">
            <Trophy size={24} strokeWidth={2} />
          </div>
          <div className="hub-page-hero__text">
            <h1 className="hub-page-hero__title">{tr("club.title")}</h1>
            <p className="hub-page-hero__subtitle muted">
              {tr("club.subtitle")}
            </p>
          </div>
        </header>
        <section
          className="hub-actions-section"
          aria-labelledby="hub-actions-heading"
        >
          <h2 id="hub-actions-heading" className="hub-section-label">
            {tr("club.actionsTitle")}
          </h2>
          <div className="hub-actions-grid hub-actions-grid--v0">
            {actions.map((a, i) => {
              const Icon = a.icon;
              const title = tr(a.titleKey);
              const description = a.descriptionKey
                ? tr(a.descriptionKey)
                : null;
              const isPlaceholder = a.href === "#" || a.href === "";
              const content = (
                <>
                  <span className="hub-action-card__icon" aria-hidden="true">
                    {Icon ? <Icon size={20} strokeWidth={2} /> : null}
                  </span>
                  <div className="hub-action-card__body">
                    <span className="hub-action-card__title">{title}</span>
                    {description ? (
                      <span className="hub-action-card__desc">
                        {description}
                      </span>
                    ) : null}
                  </div>
                  <ChevronRight
                    size={20}
                    strokeWidth={2}
                    className="hub-action-card__chevron"
                    aria-hidden
                  />
                </>
              );
              if (a.onBeforeNavigate && a.href !== "#") {
                return (
                  <Link
                    key={i}
                    to={a.href}
                    className="card hub-action-card"
                    onClick={() => a.onBeforeNavigate?.()}
                  >
                    {content}
                  </Link>
                );
              }
              if (isPlaceholder) {
                return (
                  <div
                    key={i}
                    className="card hub-action-card hub-action-card--disabled"
                  >
                    {content}
                  </div>
                );
              }
              return (
                <Link key={i} to={a.href} className="card hub-action-card">
                  {content}
                </Link>
              );
            })}
          </div>
        </section>
        <section className="hub-recent">
          <section
            className="hub-recent-section"
            aria-labelledby="hub-recent-heading"
          >
            <h2 id="hub-recent-heading" className="hub-recent-section__title">
              <Clock size={20} strokeWidth={2} aria-hidden />
              {tr("club.lastTournaments")}
            </h2>
            {list.length === 0 ? (
              <p className="muted hub-recent-section__empty hub-recent-section__empty--no-margin">
                {tr(emptyKey)}
              </p>
            ) : (
              <ul className="hub-recent-list hub-recent-list--reset">
                {list.map((item, i) => {
                  const status = item.status
                    ? String(item.status).toLowerCase().trim()
                    : null;
                  const statusKey =
                    status === "finished" ? "completed" : status;
                  const statusLabel = status
                    ? tr(`status.${status}`) || tr(`status.${statusKey}`)
                    : null;
                  const dateStr = formatDate(item.at ?? item.updatedAt);
                  return (
                    <li
                      key={item.id ?? item.code ?? i}
                      className="hub-recent-item"
                    >
                      <Link to={item.href} className="hub-recent-item__link">
                        <span
                          className="hub-recent-item__icon hub-recent-item__icon--clubmeisterschaft"
                          aria-hidden
                        >
                          <Trophy size={20} strokeWidth={2} />
                        </span>
                        <div className="hub-recent-item__body">
                          <div className="hub-recent-item__line1">
                            <span className="hub-recent-item__title">
                              {item.label}
                            </span>
                          </div>
                          <div className="hub-recent-item__line2">
                            {statusKey && statusLabel ? (
                              <span
                                className="hub-recent-item__pill"
                                data-status={statusKey}
                              >
                                {statusLabel}
                              </span>
                            ) : null}
                            {dateStr ? (
                              <span className="muted hub-recent-item__date">
                                {dateStr}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <ChevronRight
                          size={20}
                          strokeWidth={2}
                          className="hub-recent-item__chevron"
                          aria-hidden
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </section>
      </main>
    </div>
  );
}
